import Decimal from "decimal.js";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { cn } from "@/lib/utils";
import { SelectOptionItem } from "@/lib/models/ui";
import { CustomerSplitEntry } from "./types";

interface CustomerSplitsFieldProps {
  splits: CustomerSplitEntry[];
  totalQuantity: Decimal | null;
  lhcRemainder: Decimal;
  customerOptions: SelectOptionItem<string>[];
  customersLoading: boolean;
  isNonClient: boolean;
  shouldIgnore: boolean;
  error?: string;
  suggestionCustomerId?: string;
  suggestionCustomerLabel?: string;
  showCustomerSuggestion: boolean;
  onChangeQuantity: (index: number, value: string) => void;
  onChangeCustomer: (index: number, customerId: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onApplyCustomerSuggestion: () => void;
  onChangeIsNonClient: (v: boolean) => void;
  onChangeShouldIgnore: (v: boolean) => void;
  isEditable: boolean;
}

export function CustomerSplitsField({
  isEditable,
  splits,
  totalQuantity,
  lhcRemainder,
  customerOptions,
  customersLoading,
  isNonClient,
  shouldIgnore,
  error,
  suggestionCustomerId,
  suggestionCustomerLabel,
  showCustomerSuggestion,
  onChangeQuantity,
  onChangeCustomer,
  onAdd,
  onRemove,
  onApplyCustomerSuggestion,
  onChangeIsNonClient,
  onChangeShouldIgnore,
}: CustomerSplitsFieldProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Label>
          Customer(s) <span className="text-destructive">*</span>
        </Label>
        <div className="flex items-center gap-2">
          <Checkbox
            disabled={!isEditable}
            id="ttv2-is-error"
            checked={shouldIgnore}
            onCheckedChange={(c) => onChangeShouldIgnore(c === true)}
          />
          <Label htmlFor="ttv2-is-error" className="font-bold cursor-pointer">
            Is Error
          </Label>
        </div>
      </div>

      {showCustomerSuggestion && suggestionCustomerId && (
        <Button
          disabled={!isEditable}
          type="button"
          variant="outline"
          size="sm"
          onClick={onApplyCustomerSuggestion}
          className="text-xs h-7"
        >
          Apply suggestion: {suggestionCustomerLabel ?? suggestionCustomerId}
        </Button>
      )}

      <div className="space-y-2">
        {splits.map((entry, index) => (
          <div key={entry.key} className="flex gap-2 items-start">
            <div className="w-24 shrink-0">
              <input
                disabled={!isEditable}
                type="number"
                min="0"
                step="1"
                value={entry.quantity}
                onChange={(e) => onChangeQuantity(index, e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                placeholder="Qty"
              />
            </div>
            <div className="flex-1">
              <Combobox
                dwimOnChange
                isModal={false}
                placeholder="Select customer..."
                options={customerOptions}
                value={entry.customer_id}
                onValueChange={(val) => onChangeCustomer(index, val)}
                disabled={customersLoading || !isEditable}
                virtualizeList
                popoverClassName="w-[420px] h-[300px]"
              />
            </div>
            {splits.length > 1 && (
              <Button
                disabled={!isEditable}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemove(index)}
                className="h-9 px-2 text-destructive hover:text-destructive"
              >
                ✕
              </Button>
            )}
          </div>
        ))}

        {totalQuantity != null && (
          <div className="flex gap-2 items-start">
            <div className="w-24 shrink-0">
              <input
                type="text"
                value={lhcRemainder.toFixed(0)}
                readOnly
                disabled
                className={cn(
                  "w-full h-9 rounded-md border border-input px-3 py-1 text-sm shadow-sm",
                  "bg-muted text-muted-foreground cursor-not-allowed"
                )}
              />
            </div>
            <div className="flex-1 flex items-center h-9 px-3 rounded-md border border-input bg-muted text-muted-foreground text-sm">
              LHC (remainder)
            </div>
          </div>
        )}

        {totalQuantity != null && splits.length > 1 && (
          <div className="flex gap-2 items-center pt-1 border-t border-border">
            <div className="w-24 shrink-0 text-right pr-3 text-sm font-medium tabular-nums">
              {totalQuantity.toFixed(0)}
            </div>
            <div className="flex-1 text-sm text-muted-foreground">Total</div>
          </div>
        )}
      </div>

      <div className="flex flex-row gap-4 items-center">
        <Button disabled={!isEditable} type="button" variant="outline" size="sm" onClick={onAdd} className="mt-1">
          + Add Split
        </Button>
        <div className="flex items-center gap-2">
          <Checkbox
            disabled={!isEditable}
            id="ttv2-non-client"
            checked={isNonClient}
            onCheckedChange={(c) => onChangeIsNonClient(c === true)}
          />
          <Label htmlFor="ttv2-non-client" className="font-bold cursor-pointer">
            Non Client
          </Label>
        </div>
      </div>

      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}
